import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, SendHorizonal, Sparkles, Trash2 } from "lucide-react";
import { api, API_BASE, errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ChatMessage } from "@/components/assistant/ChatMessage";
import { cn } from "@/lib/utils";

async function streamAsk(sessionId, text, onEvent) {
  const res = await fetch(`${API_BASE}/assistant/sessions/${sessionId}/messages`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json", Accept: "text/event-stream" }, body: JSON.stringify({ text }) });
  if (!res.ok) { const j = await res.json().catch(() => ({})); throw new Error(typeof j.detail === "string" ? j.detail : res.statusText); }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    let idx;
    while ((idx = buf.indexOf("\n\n")) >= 0) {
      const chunk = buf.slice(0, idx); buf = buf.slice(idx + 2);
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (line) onEvent(JSON.parse(line.slice(6)));
    }
  }
}

export default function AskPage() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const sessionId = params.get("s");
  const [text, setText] = useState("");
  const [live, setLive] = useState(null);
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);
  const { data: info } = useQuery({ queryKey: ["assistant-info"], queryFn: () => api.get("/assistant/info").then((r) => r.data) });
  const { data: sessions } = useQuery({ queryKey: ["assistant-sessions"], queryFn: () => api.get("/assistant/sessions").then((r) => r.data.items) });
  const { data: thread } = useQuery({ queryKey: ["assistant-thread", sessionId], queryFn: () => api.get(`/assistant/sessions/${sessionId}/messages`).then((r) => r.data), enabled: !!sessionId });
  const create = useMutation({ mutationFn: () => api.post("/assistant/sessions").then((r) => r.data), onSuccess: (s) => { qc.invalidateQueries({ queryKey: ["assistant-sessions"] }); setParams({ s: s.id }); }, onError: (e) => toast.error(errMsg(e)) });
  const remove = useMutation({ mutationFn: (id) => api.delete(`/assistant/sessions/${id}`), onSuccess: () => { qc.invalidateQueries({ queryKey: ["assistant-sessions"] }); setParams({}); }, onError: (e) => toast.error(errMsg(e)) });
  const messages = [...(thread?.items || []), ...(live ? [live.user, live.assistant] : [])];
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length, live?.assistant?.content]);

  const send = async (q) => {
    const question = (q ?? text).trim();
    if (!question || busy) return;
    let sid = sessionId;
    if (!sid) { try { const s = await api.post("/assistant/sessions").then((r) => r.data); sid = s.id; setParams({ s: sid }); qc.invalidateQueries({ queryKey: ["assistant-sessions"] }); } catch (e) { toast.error(errMsg(e)); return; } }
    setText(""); setBusy(true);
    const state = { user: { id: "live-u", role: "user", content: question }, assistant: { id: "live-a", role: "assistant", content: "", tools: [], streaming: true } };
    setLive({ ...state });
    try {
      await streamAsk(sid, question, (ev) => {
        if (ev.type === "delta") state.assistant.content += ev.content;
        else if (ev.type === "tool") { const t = state.assistant.tools.find((x) => x.name === ev.name && x.status === "start"); if (ev.status === "start") state.assistant.tools.push({ name: ev.name, status: "start" }); else if (t) Object.assign(t, { status: "done", args: ev.args, error: ev.error }); else state.assistant.tools.push({ name: ev.name, status: "done", args: ev.args, error: ev.error }); }
        else if (ev.type === "error") state.assistant.error = ev.detail;
        else if (ev.type === "done") state.assistant.streaming = false;
        setLive({ user: state.user, assistant: { ...state.assistant, tools: [...state.assistant.tools] } });
      });
    } catch (e) { state.assistant.error = e.message; setLive({ user: state.user, assistant: { ...state.assistant, streaming: false } }); }
    setBusy(false);
    await qc.invalidateQueries({ queryKey: ["assistant-thread", sid] });
    await qc.invalidateQueries({ queryKey: ["assistant-sessions"] });
    setLive(null);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)]">
      <PageHeader title="Ask TQI" description={`Conversational analyst over the unified activity timeline (model: ${info?.model || "…"}). Admin-only; every question is audit-logged; answers cite events and say Unavailable when a source is not connected.`} actions={<Button size="sm" className="bg-[#101C47] hover:bg-[#1B2D6E] text-white" onClick={() => create.mutate()} data-testid="ask-new-session"><Plus className="mr-1 h-4 w-4" />New conversation</Button>} />
      <div className="grid flex-1 min-h-0 gap-4 lg:grid-cols-[260px_1fr]">
        <aside className="hidden lg:flex flex-col tqi-card overflow-hidden" data-testid="ask-sessions">
          <div className="border-b border-slate-200 px-3 py-2 text-xs font-bold uppercase tracking-wider text-slate-500">Conversations</div>
          <div className="flex-1 overflow-y-auto">
            {(sessions || []).length === 0 && <div className="px-3 py-6 text-xs text-slate-500">No conversations yet.</div>}
            {(sessions || []).map((s) => (
              <div key={s.id} className={cn("group flex items-start justify-between gap-1 border-b border-slate-100 px-3 py-2 text-xs cursor-pointer hover:bg-[#EEF2F9]", s.id === sessionId && "bg-[#EEF2F9]")} onClick={() => setParams({ s: s.id })} data-testid={`ask-session-${s.id}`}>
                <div className="min-w-0"><div className="truncate font-semibold text-[#101C47]">{s.title}</div><div className="text-slate-500">{s.turns} turn(s)</div></div>
                <button className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-[#F92626]" onClick={(e) => { e.stopPropagation(); remove.mutate(s.id); }} aria-label="Delete" data-testid={`ask-session-delete-${s.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>))}
          </div>
        </aside>
        <section className="flex min-h-0 flex-col tqi-card overflow-hidden" data-testid="ask-chat">
          <div className="flex-1 overflow-y-auto bg-[#F6F7FB] p-4 space-y-4">
            {messages.length === 0 && (
              <div className="mx-auto max-w-2xl pt-6 text-center" data-testid="ask-empty">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-[#101C47] text-white"><Sparkles className="h-5 w-5" /></div>
                <h2 className="mt-3 text-base md:text-lg font-bold text-[#101C47]">Ask about who did what, when</h2>
                <p className="mt-1 text-sm text-slate-600">Try one of these, or type your own question. Dates are interpreted in America/New_York.</p>
                <div className="mt-4 grid gap-2 sm:grid-cols-2">{(info?.suggestions || []).map((s) => <button key={s} onClick={() => send(s)} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-left text-xs text-slate-700 hover:border-[#101C47] hover:text-[#101C47] transition-colors" data-testid="ask-suggestion">{s}</button>)}</div>
              </div>
            )}
            {messages.map((m) => <ChatMessage key={m.id} m={m} />)}
            <div ref={bottomRef} />
          </div>
          <div className="border-t border-slate-200 bg-white p-3">
            <div className="flex items-end gap-2">
              <Textarea value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder='e.g. "What was Dawn Cobb doing around 10:30 AM on 10/06?"' rows={2} className="min-h-[44px] resize-none" disabled={busy} data-testid="ask-input" />
              <Button className="h-11 bg-[#F92626] hover:bg-[#d91f1f] text-white" disabled={busy || !text.trim()} onClick={() => send()} data-testid="ask-send"><SendHorizonal className="h-4 w-4" /></Button>
            </div>
            <div className="mt-1 text-[11px] text-slate-500">Enter to send · Shift+Enter for a new line · {busy ? "Answering…" : "Ready"}</div>
          </div>
        </section>
      </div>
    </div>
  );
}
